import React from 'react';
import { useCurrentFrame, useVideoConfig, delayRender, continueRender, cancelRender } from 'remotion';
import { stagedMotionStyle } from './component-motion.mjs';
import { prepareMotionLayout, restoreElementStyle } from './layout-baseline.mjs';
import { isLiquidEntrance, isEdgeEntrance, liquidEntranceDuration, prepareLiquidLetters, applyLiquidLetters, applyEdgeLetters } from './liquid-letters.mjs';
import { isPaperEntrance, paperDuration, preparePaper } from './paper-unfold.mjs';

// An isolated document keeps imported CSS out of the editor and scopes all lookups.
export function AnimatedHtml(props) {
  return <HtmlFrame key={props.html} {...props} />;
}

function HtmlFrame({ html, attributeAnimations = [], selectedElementId, onElementCapabilities }) {
  const frame = useCurrentFrame();
  const { fps, width, height, durationInFrames } = useVideoConfig();
  const iframe = React.useRef(null);
  const elements = React.useRef(new Map());
  const prepared = React.useRef({ key: null, elements: new Map() });
  const liquid = React.useRef(new Map());
  const paper = React.useRef([]);
  const [ready, setReady] = React.useState(false);
  const [handle] = React.useState(() => delayRender('Load HTML template assets'));
  React.useEffect(() => () => continueRender(handle), [handle]);
  const source = React.useMemo(() => `<html><head><meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src https: data: 'self'; style-src 'unsafe-inline'; font-src https: data: 'self'"><style>html,body{margin:0;width:${width}px;height:${height}px;overflow:hidden}</style></head><body>${html}<style>*,*::before,*::after{animation:none!important;transition:none!important}</style></body></html>`, [html, width, height]);

  const load = async () => {
    const doc = iframe.current?.contentDocument;
    if (!doc) return;
    try {
      await Promise.all([...doc.images].map(img => img.complete ? Promise.resolve() : new Promise(resolve => {
        img.addEventListener('load', resolve, { once: true });
        img.addEventListener('error', resolve, { once: true });
      })));
      await doc.fonts.ready;
      if (iframe.current?.contentDocument !== doc) return;
      const capabilities = {};
      elements.current.clear();
      prepared.current.key = null;
      doc.querySelectorAll('[data-bnb-motion-id], [data-bnb-post-node], [data-bnb-node]').forEach(element => {
        const id = element.getAttribute('data-bnb-motion-id') || element.getAttribute('data-bnb-post-node') || element.getAttribute('data-bnb-node');
        const computed = doc.defaultView.getComputedStyle(element);
        const inline = computed.display === 'inline' && !['IMG', 'VIDEO', 'CANVAS'].includes(element.tagName);
        capabilities[id] = { inline, text: !!element.textContent.trim() && !['STYLE','SCRIPT','SVG','TEXTAREA'].includes(element.tagName) };
        elements.current.set(id, {
          element, inline, opacity: Number(computed.opacity), transform: computed.transform,
          style: element.getAttribute('style'),
        });
      });
      onElementCapabilities?.(capabilities);
      setReady(true);
    } catch (error) { cancelRender(error); }
  };

  React.useLayoutEffect(() => {
    if (!ready) return;
    for (const item of liquid.current.values()) item.restore();
    liquid.current.clear();
    for (const item of paper.current) item.restore();
    paper.current = [];
    const effects = new Map(attributeAnimations.map(a => [a.nodeId, a.preset]));
    const key = JSON.stringify(attributeAnimations);
    if (prepared.current.key !== key) prepared.current = { key, elements: prepareMotionLayout(elements.current, effects) };
    const staged = attributeAnimations.some(a => typeof a.preset === 'object');
    const baselines = !staged && frame / fps >= 0.8 ? elements.current : prepared.current.elements;
    for (const [id, baseline] of baselines) {
      const { element, opacity, transform, inline } = baseline;
      restoreElementStyle(baseline);
      const value = effects.get(id) || 'none';
      const customEntrance = isLiquidEntrance(value) || isEdgeEntrance(value) || isPaperEntrance(value);
      const preset = customEntrance ? typeof value === 'object' ? { ...value, in:'none' } : 'none' : value;
      if (preset !== 'none') {
        const motion = stagedMotionStyle(preset, frame, fps, durationInFrames, inline);
        if (motion.opacity !== 1) element.style.setProperty('opacity', String(opacity * motion.opacity), 'important');
        if (!inline && (motion.translate !== '0px 0px' || motion.scale !== '1')) {
          const [x, y] = motion.translate.split(' ');
          element.style.setProperty('transform', `translate(${x}, ${y}) scale(${motion.scale}) ${transform === 'none' ? '' : transform}`, 'important');
        }
      }
      if (id === selectedElementId) {
        element.style.setProperty('outline', '3px solid #4a7bff', 'important');
        element.style.setProperty('outline-offset', '-3px', 'important');
      }
    }
    // Restore the original text after entrance: exact kerning, line breaks and
    // final stills remain identical to the imported HTML. Rebuild when seeking.
    const liquidTargets = [...elements.current].filter(([id]) => isLiquidEntrance(effects.get(id)) || isEdgeEntrance(effects.get(id)));
    const targetElements = new Set(liquidTargets.map(([, baseline]) => baseline.element));
    if (frame / fps < liquidEntranceDuration(fps, durationInFrames)) {
      for (const [id, { element }] of liquidTargets) {
        const item = prepareLiquidLetters(element, targetElements);
        liquid.current.set(id, item);
        if (isEdgeEntrance(effects.get(id))) applyEdgeLetters(item, frame, fps, durationInFrames);
        else applyLiquidLetters(item, frame, fps, durationInFrames);
      }
    }
    if (frame / fps < paperDuration(fps,durationInFrames)) {
      for (const [id,{element}] of elements.current) if(isPaperEntrance(effects.get(id))) {
        const item=preparePaper(element,frame,fps,durationInFrames);
        if(item){
          paper.current.push(item);
          const paperHandle=delayRender('Decode unfolded paper frame');
          item.ready.then(()=>continueRender(paperHandle)).catch(error=>cancelRender(error));
        }
      }
    }
    continueRender(handle);
  }, [ready, frame, fps, durationInFrames, attributeAnimations, selectedElementId, handle]);

  return <iframe ref={iframe} title="Animated card" sandbox="allow-same-origin" srcDoc={source} onLoad={load}
    style={{ width: '100%', height: '100%', border: 0, background: 'transparent', pointerEvents: 'none' }} />;
}
